import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { dict } from "@/lib/i18n";

const AppCtx = createContext(null);
export const useApp = () => useContext(AppCtx);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "it");
  const [config, setConfig] = useState({ service_fee_pct: 0.12, maintenance: false });
  const [favorites, setFavorites] = useState([]);

  const t = useCallback((path) => {
    const parts = path.split(".");
    let cur = dict[lang] || dict.it;
    for (const p of parts) { cur = cur?.[p]; if (cur == null) return path; }
    return cur;
  }, [lang]);

  const changeLang = (l) => { setLang(l); localStorage.setItem("lang", l); };

  const refreshFavorites = useCallback(async () => {
    try {
      const { data } = await api.get("/favorites");
      setFavorites(data.map((p) => p.pool_id));
    } catch { setFavorites([]); }
  }, []);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    const token = localStorage.getItem("token");
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      refreshFavorites();
    } catch {
      localStorage.removeItem("token");
    } finally { setLoading(false); }
  }, [refreshFavorites]);

  useEffect(() => { checkAuth(); }, [checkAuth]);
  useEffect(() => {
    const load = () => api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("token", data.token);
    setUser(data.user);
    refreshFavorites();
    return data.user;
  };
  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("token", data.token);
    setUser(data.user);
    return data.user;
  };
  const logout = () => { localStorage.removeItem("token"); setUser(null); setFavorites([]); };

  return (
    <AppCtx.Provider value={{ user, setUser, loading, lang, changeLang, t, login, register, logout, config, favorites, refreshFavorites }}>
      {children}
    </AppCtx.Provider>
  );
}
